import { useState, type ReactNode } from "react";

import { ApiError } from "../../api/client.ts";
import { useLookups, type CatalogItem } from "../../api/hooks.ts";
import { useCreateNote, useUpdateNote, type Note } from "../../api/notes.ts";
import { Modal } from "../../ui/Modal.tsx";
import { SaveButton } from "../../ui/SaveButton.tsx";
import { Suggestions } from "../../ui/Suggestions.tsx";

/**
 * "Nueva nota": el modal corto de la plantilla.
 *
 * Existe porque la mitad de lo que aprende un trader no cabe dentro de una
 * operacion. La regla que te acabas de poner, lo que viste y no operaste, el
 * aviso a ti mismo de manana: si solo se pudiera escribir dentro de una
 * operacion, todo eso se perderia o —peor— se inventaria una operacion para
 * poder escribirlo.
 */
export function NoteModal(props: { note?: Note; onClose: () => void }): ReactNode {
  const lookups = useLookups();
  const create = useCreateNote();
  const update = useUpdateNote(props.note?.id ?? "");

  const [text, setText] = useState(props.note?.text ?? "");
  const [moodId, setMoodId] = useState(props.note?.moodId ?? "");

  const saving = create.isPending || update.isPending;
  const failure = create.error ?? update.error;
  const error = failure instanceof ApiError ? failure : null;
  const emotions = (lookups.data?.catalogs["emociones"] ?? []).filter((item) => item.isActive);

  const dirty = text !== (props.note?.text ?? "") || moodId !== (props.note?.moodId ?? "");
  const editing = props.note !== undefined;

  function save(): void {
    const body = {
      text: text.trim(),
      moodId: moodId === "" ? null : moodId,
      postedAtUtc: props.note?.postedAtUtc ?? new Date().toISOString(),
      ...(editing ? { version: props.note?.version } : {}),
    };

    const done = { onSuccess: props.onClose };

    if (editing) {
      update.mutate(body, done);
    } else {
      create.mutate(body, done);
    }
  }

  return (
    <Modal
      narrow
      dirty={dirty}
      title={editing ? "Editar nota" : "Nueva nota"}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <SaveButton
            label={editing ? "Guardar" : "Publicar nota"}
            icon="msg"
            busy={saving}
            saved={false}
            disabled={text.trim() === ""}
            onClick={save}
          />
        </>
      }
    >
      {error !== null && (
        <p className="errbox">
          {error.isConcurrencyConflict
            ? "Alguien la cambio desde otro dispositivo. Cierra, recarga y vuelve a intentarlo."
            : error.message}
        </p>
      )}

      <label className="f">
        <span>Que quieres dejar escrito</span>
        <textarea
          className="ctl tw-area"
          value={text}
          autoFocus
          maxLength={2000}
          placeholder="La regla que te acabas de poner, lo que viste y no operaste, el aviso a ti mismo de manana."
          onChange={(event) => setText(event.target.value)}
        />
        {error?.fieldError("text") !== undefined && (
          <span className="err">{error.fieldError("text")}</span>
        )}
        <span className="hint">{2000 - text.length} caracteres libres</span>
      </label>

      <Suggestions field="notes" onPick={(frase) => setText(text === "" ? frase : `${text}\n${frase}`)} />

      <div className="fblock" style={{ marginTop: 12 }}>
        <span>Como te sientes hoy</span>
        <div className="emo-picker">
          {emotions.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`emo ${polarity(item)} ${moodId === item.id ? "sel" : ""}`}
              aria-pressed={moodId === item.id}
              onClick={() => setMoodId(moodId === item.id ? "" : item.id)}
            >
              {item.emoji !== null && <span aria-hidden="true">{item.emoji}</span>}
              {item.name}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function polarity(item: CatalogItem): string {
  if (item.polarity === "positive") return "good";
  if (item.polarity === "negative") return "bad";

  return "warn";
}
